import moment from 'moment-timezone';
import React, { useMemo, useRef } from 'react';
import type ReactDatePicker from 'react-datepicker';
import styled from 'styled-components';
import { useUniqueName } from '../../hooks/useUniqueName';
import { FONT_SIZE } from '../../styles';
import { BareDatePicker } from '../DatePicker/BareDatePicker';
import { dateToDayAndTime, useDatePickerDateAndTime } from '../DatePicker/datePickerHelpers';
import FieldSet from '../FieldSet/FieldSet';
import { TimePicker } from '../TimePicker';
import { type DateTimePickerProps } from './DateTimePicker.types';

/**
 * DateTimePicker allows the user to pick a date and time.  The `value` and
 * the `onChange` value are standard Date objects, and will be correct for the
 * passed in `timeZoneId`.  If the `timeZoneId` changes while the date picker is
 * active, the chosen values will change to reflect the new time zone.
 */
const DateTimePicker: React.FC<DateTimePickerProps> = (props) => {
  const {
    value,
    onChange,
    onBlur: originalOnBlur,
    timeZoneId,
    locale,
    disallowFuture,
    label,
    errorText,
    hintText,
    startTime,
    endTime,
    minTime,
    maxTime,
    hideSeconds,
    stacked,
    required,
    hideTimeZoneAbbr,
    ...rest
  } = props;

  const datePickerRef = useRef<ReactDatePicker | null>(null);
  const timePickerRef = useRef<HTMLInputElement | null>(null);

  // When the user is editing the time/date, we will let them pick a time or date
  // outside the valid bounds.  If we try to stop them, it leads to weirdness in
  // the time picker (e.g. if the min time is 9:10am, you can't type "9:20",
  // because when you type "2" it will be 9:02, and we'll 'fix' it).  We "fix"
  // any bad inputs when the input looses focus.
  let errored = false;
  if (maxTime && value && value > maxTime) {
    errored = true;
  } else if (minTime && value && value < minTime) {
    errored = true;
  }

  const { date, time, setDate, setTime, onBlur } = useDatePickerDateAndTime(
    value,
    timeZoneId,
    onChange,
    {
      onBlur: originalOnBlur,
      hideSeconds,
      disallowFuture,
      minTime,
      maxTime,
    },
  );

  const timeZoneAbbr = useMemo(() => moment.tz(value, timeZoneId).zoneAbbr(), [value, timeZoneId]);
  const name = useUniqueName(props.name);

  let actualMaxTime = maxTime;
  if (disallowFuture && (!maxTime || maxTime.valueOf() > Date.now())) {
    actualMaxTime = new Date();
  }

  return (
    <FieldSet
      colorMode={props.colorMode}
      name={name}
      label={label}
      errorText={errorText}
      error={!!errorText || errored}
      hintText={hintText}
    >
      <Wrapper stacked={stacked}>
        <BareDatePicker
          {...rest}
          locale={locale}
          value={date}
          onChange={setDate}
          onCalendarClose={() => {
            onBlur?.();
            timePickerRef.current?.focus();
          }}
          onBlur={onBlur}
          ref={datePickerRef}
          minDate={dateToDayAndTime(minTime, timeZoneId)[0]}
          maxDate={dateToDayAndTime(actualMaxTime, timeZoneId)[0]}
          startDate={dateToDayAndTime(startTime, timeZoneId)[0]}
          endDate={dateToDayAndTime(endTime, timeZoneId)[0]}
          name={name}
          showTodayButton
        />
        <TimePickerWrapper>
          <TimePicker
            ref={timePickerRef}
            value={time}
            disabled={!!props.disabled}
            onChange={setTime}
            onBlur={onBlur}
            showSecond={!hideSeconds}
            inline
            required
          />
          {!hideTimeZoneAbbr && <Timezone>&nbsp;{timeZoneAbbr}</Timezone>}
        </TimePickerWrapper>
      </Wrapper>
    </FieldSet>
  );
};

export default React.memo(DateTimePicker);

const Wrapper = styled.div<{ stacked?: boolean }>`
  display: flex;
  flex-direction: ${({ stacked }) => (stacked ? 'column' : 'row')};

  > * {
    flex: 1 1 auto;
  }
`;

const TimePickerWrapper = styled.span`
  display: inline-flex;
  > * {
    flex: 1 1 auto;
  }
`;

const Timezone = styled.span`
  font-weight: 400;
  font-size: ${FONT_SIZE['14']};
  line-height: 36px;
  flex: 0 0 auto;
`;
